import { Image, Empty, Spin, Button } from "antd";
import { Eye } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getChannelCustomerPhotos } from "../../../api/ChannelCustomer";

// Số ảnh hiển thị mỗi trang (phân trang phía client)
const PAGE_LIMIT = 9;

export default function CustomerImgList({ customerId }: { customerId?: number }) {
    const [images, setImages] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);

    const loadPhotos = useCallback((showSpinner = true) => {
        if (!customerId) {
            setImages([]);
            return;
        }
        if (showSpinner) setLoading(true);
        getChannelCustomerPhotos(customerId, { page: 1, limit: PAGE_LIMIT })
            .then((res) => {
                const urls = (res.data || [])
                    .map((p) => p.image_path)
                    .filter((u): u is string => !!u);
                setImages(urls);
            })
            .finally(() => setLoading(false));
    }, [customerId]);

    useEffect(() => {
        setPage(1);
        loadPhotos();
    }, [loadPhotos]);

    // Có ảnh mới trong hội thoại -> nạp lại danh sách ảnh khách gửi (không hiện spinner để mượt)
    useEffect(() => {
        const onNewPhoto = (e: any) => {
            if (!customerId) return;
            // chỉ refetch khi sự kiện thuộc đúng khách đang mở (hoặc không kèm id)
            if (e?.detail && Number(e.detail) !== Number(customerId)) return;
            loadPhotos(false);
        };
        window.addEventListener("customer-photos-updated", onNewPhoto);
        return () => window.removeEventListener("customer-photos-updated", onNewPhoto);
    }, [customerId, loadPhotos]);

    const visibleImages = useMemo(() => images.slice(0, page * PAGE_LIMIT), [images, page]);
    const remaining = images.length - visibleImages.length;

    return (
        <div>
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-[#8c8c8c]">
                    Tổng hợp ảnh từ hội thoại
                </span>
                {images.length > 0 && (
                    <span className="text-[10px] text-[#8c8c8c]">
                        {visibleImages.length}/{images.length}
                    </span>
                )}
            </div>

            {/* Images */}
            {loading ? (
                <div className="flex justify-center py-4">
                    <Spin size="small" />
                </div>
            ) : images.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có ảnh" />
            ) : (
                <>
                    <div className="max-h-[300px] overflow-y-auto pr-1">
                        <Image.PreviewGroup>
                            <div className="flex flex-wrap gap-2">
                                {visibleImages.map((src, index) => (
                                    <div className="flex-shrink-0 object-cover border rounded-md overflow-hidden h-[84px] w-[84px]" key={index}>
                                        <Image
                                            src={src}
                                            width={84}
                                            height={84}
                                            className="object-cover"
                                            preview={{
                                                mask: (
                                                    <div>
                                                        <Eye size={20} />
                                                    </div>
                                                )
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </Image.PreviewGroup>
                    </div>

                    {remaining > 0 && (
                        <div className="flex justify-center mt-2">
                            <Button size="small" type="link" onClick={() => setPage((p) => p + 1)}>
                                Xem thêm ({remaining})
                            </Button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
